import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { WorkLogEntry } from '../../shared/types';

const MAX_DAYS_SHOWN = 120;

export type PrecomputedDailyPoint = {
  date: string;
  timeSpent: number;
  developers: Array<{ name: string; hours: number }>;
};

interface DailyTimeChartProps {
  data: WorkLogEntry[];
  /** When set (e.g. from /api/analytics/chart-series), chart does not depend on full worklog in memory. */
  precomputedDaily?: PrecomputedDailyPoint[];
}

interface DailyDataItem {
  label: string;
  date: string;
  timeSpent: number;
  developers: Array<{ name: string; hours: number }>;
}

function calendarDayKey(startDate: string): string | null {
  const t = startDate.trim();
  if (t.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(t)) {
    return t.slice(0, 10);
  }
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

export const DailyTimeChart: React.FC<DailyTimeChartProps> = ({ data, precomputedDaily }) => {
  const chartRows: DailyDataItem[] = useMemo(() => {
    if (precomputedDaily?.length) {
      const sorted = [...precomputedDaily].sort((a, b) => a.date.localeCompare(b.date));
      const slice = sorted.length > MAX_DAYS_SHOWN ? sorted.slice(-MAX_DAYS_SHOWN) : sorted;
      return slice.map((row) => ({
        label: row.date.length >= 10 ? row.date.slice(5) : row.date,
        date: row.date,
        timeSpent: row.timeSpent,
        developers: row.developers ?? [],
      }));
    }

    const acc: Record<string, { timeSpent: number; developers: Record<string, number> }> = {};
    for (const entry of data) {
      const key = calendarDayKey(entry.startDate);
      if (!key) continue;
      if (!acc[key]) {
        acc[key] = { timeSpent: 0, developers: {} };
      }
      acc[key].timeSpent += entry.timeSpent;
      if (!acc[key].developers[entry.author]) {
        acc[key].developers[entry.author] = 0;
      }
      acc[key].developers[entry.author] += entry.timeSpent;
    }

    const sortedKeys = Object.keys(acc).sort((a, b) => a.localeCompare(b));
    const keys =
      sortedKeys.length > MAX_DAYS_SHOWN ? sortedKeys.slice(-MAX_DAYS_SHOWN) : sortedKeys;

    return keys.map((date) => {
      const dayData = acc[date];
      const developersArray = Object.entries(dayData.developers)
        .map(([name, hours]) => ({ name, hours }))
        .sort((a, b) => b.hours - a.hours);
      return {
        label: date.length >= 10 ? date.slice(5) : date,
        date,
        timeSpent: dayData.timeSpent,
        developers: developersArray,
      };
    });
  }, [data, precomputedDaily]);

  if (chartRows.length === 0) {
    return (
      <div
        className="dashboard-card recharts-bar-chart"
        style={{
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <p style={{ color: 'var(--color-text-secondary)' }}>No data to display</p>
      </div>
    );
  }

  return (
    <div
      className="dashboard-card recharts-bar-chart"
      title="Visual representation of total time spent per day across all developers. Helps identify daily workload patterns and peak working days."
    >
      <h3>
        Daily Time Registered
        <span
          style={{
            marginLeft: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--color-text-secondary)',
            fontWeight: 'normal',
          }}
          title="Shows aggregated work time per calendar day (UTC date from worklog start). Blue bars are total hours logged that day."
        >
          ℹ️
        </span>
      </h3>
      <div style={{ height: '300px', overflow: 'visible' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartRows} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              minTickGap={14}
              label={{ value: 'Day (MM-DD)', position: 'insideBottom', offset: 0 }}
            />
            <YAxis
              label={{ value: 'Time Spent (H)', angle: -90, position: 'insideLeft' }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const row = payload[0].payload as DailyDataItem;
                  return (
                    <div
                      style={{
                        backgroundColor: 'var(--color-bg-elevated)',
                        padding: '12px',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                        minWidth: '200px',
                      }}
                    >
                      <p
                        style={{
                          margin: '0 0 8px 0',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          color: 'var(--color-text)',
                        }}
                      >
                        {row.date}
                      </p>
                      <p
                        style={{
                          margin: '0 0 12px 0',
                          color: 'var(--color-text-secondary)',
                          fontSize: '13px',
                          fontWeight: '600',
                        }}
                      >
                        Total: {row.timeSpent.toFixed(1)} h.
                      </p>
                      {row.developers && row.developers.length > 0 ? (
                        <>
                          <div
                            style={{
                              marginTop: '8px',
                              paddingTop: '8px',
                              borderTop: '1px solid var(--color-border)',
                            }}
                          >
                            <p
                              style={{
                                margin: '0 0 8px 0',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: 'var(--color-text)',
                              }}
                            >
                              Team Member:
                            </p>
                            {row.developers.map((dev) => (
                              <p
                                key={dev.name}
                                style={{
                                  margin: '4px 0',
                                  fontSize: '12px',
                                  color: 'var(--color-text-secondary)',
                                }}
                              >
                                {dev.name}: {dev.hours.toFixed(1)} h.
                              </p>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p
                          style={{
                            margin: '8px 0 0 0',
                            fontSize: '12px',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          No data
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="timeSpent" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DailyTimeChart;
